import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./InviteShareModal.css";
import { buildChannelSharePresets } from "../../config/app/referralShareCopy";
import {
  REFERRAL_INVITE_LANDINGS,
  buildInviteLandingUrl,
  getInviteLanding,
} from "../../config/app/referralInviteLandings";
import { copyTextToClipboard } from "../../services/api/editor/share";

/**
 * Light share preview sheet: tool landing picker + editable preset + share CTA.
 */
const InviteShareModal = ({
  channelId,
  icon,
  referralCode,
  inviterName,
  planKey,
  initialLandingId = "general",
  /** When true, hide landing picker and keep initialLandingId */
  lockLanding = false,
  /** Optional copy overrides (e.g. post-download tool share) */
  toolLabel = null,
  toolBlurb = null,
  onClose,
  onShared,
}) => {
  const [landingId, setLandingId] = useState(initialLandingId || "general");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [messageTouched, setMessageTouched] = useState(false);
  const [subjectTouched, setSubjectTouched] = useState(false);

  const landing = useMemo(() => getInviteLanding(landingId), [landingId]);
  const inviteUrl = useMemo(
    () => buildInviteLandingUrl(referralCode, landing.path),
    [referralCode, landing.path]
  );

  const resolvedToolLabel = toolLabel || landing.label;
  const resolvedToolBlurb = toolBlurb || landing.blurb;

  const channel = useMemo(() => {
    const presets = buildChannelSharePresets({
      inviteUrl,
      inviterName,
      planKey,
      toolLabel: resolvedToolLabel,
      toolBlurb: resolvedToolBlurb,
    });
    return presets[channelId] || null;
  }, [
    channelId,
    inviteUrl,
    inviterName,
    planKey,
    resolvedToolBlurb,
    resolvedToolLabel,
  ]);

  useEffect(() => {
    setLandingId(initialLandingId || "general");
    setMessageTouched(false);
    setSubjectTouched(false);
    setStatus("");
    setBusy(false);
  }, [channelId, initialLandingId]);

  useEffect(() => {
    if (!channel) return;
    if (!messageTouched) setMessage(channel.message || "");
    if (!subjectTouched) setSubject(channel.subject || "");
  }, [channel, messageTouched, subjectTouched]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!channel) return null;

  const handlePrimary = async () => {
    const text = message.trim();
    if (!text) {
      setStatus("Add a short message before sharing.");
      return;
    }

    setBusy(true);
    setStatus("");

    try {
      if (channel.mode === "copy-open") {
        const ok = await copyTextToClipboard(text);
        if (!ok) {
          setStatus("Could not copy. Select the text and copy manually.");
          return;
        }
        setStatus("Copied — paste it in the app.");
        if (channel.openUrl) {
          window.open(channel.openUrl, "_blank", "noopener,noreferrer");
        }
        onShared?.(channel.id, "copy-open");
      } else {
        const url =
          channel.id === "email"
            ? channel.buildUrl?.(text, subject.trim())
            : channel.buildUrl?.(text);
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
          onShared?.(channel.id, "open");
        }
      }
      window.setTimeout(() => onClose?.(), 500);
    } catch {
      setStatus("Something went wrong. Try Copy message instead.");
    } finally {
      setBusy(false);
    }
  };

  const handleCopyOnly = async () => {
    const ok = await copyTextToClipboard(message.trim());
    if (ok) {
      setStatus("Message copied.");
      onShared?.(channel.id, "copy");
    } else {
      setStatus("Could not copy. Select the text and copy manually.");
    }
  };

  const handleCopyLink = async () => {
    const ok = await copyTextToClipboard(inviteUrl);
    setStatus(ok ? "Invite link copied." : "Could not copy link.");
  };

  return createPortal(
    <div
      className="invite-share-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-share-modal-title"
    >
      <button
        type="button"
        className="invite-share-modal__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="invite-share-modal__panel">
        <header className="invite-share-modal__head">
          <div className="invite-share-modal__brand">
            {icon ? (
              <img src={icon} alt="" className="invite-share-modal__icon" />
            ) : (
              <span className="invite-share-modal__mark">{channel.mark}</span>
            )}
            <div>
              <h2 id="invite-share-modal-title">{channel.headline}</h2>
              <p className="invite-share-modal__tip">{channel.tip}</p>
            </div>
          </div>
          <button
            type="button"
            className="invite-share-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="invite-share-modal__encourage">
          <strong>Why share?</strong> Friends get{" "}
          <span>20% extra credits</span> on their first plan — you earn when
          they subscribe. Tool links also help LazyStudio show up in search.
        </div>

        {lockLanding ? (
          <div className="invite-share-modal__field">
            <span>Friends open this tool page</span>
            <p className="invite-share-modal__locked-landing">
              {resolvedToolLabel}
            </p>
            <span className="invite-share-modal__field-hint">{landing.hint}</span>
          </div>
        ) : (
          <label className="invite-share-modal__field">
            <span>Send friends to this tool page</span>
            <select
              className="invite-share-modal__select"
              value={landingId}
              onChange={(e) => {
                setLandingId(e.target.value);
                setMessageTouched(false);
                setSubjectTouched(false);
              }}
            >
              {REFERRAL_INVITE_LANDINGS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            <span className="invite-share-modal__field-hint">{landing.hint}</span>
          </label>
        )}

        {channel.id === "email" ? (
          <label className="invite-share-modal__field">
            <span>Subject</span>
            <input
              value={subject}
              onChange={(e) => {
                setSubjectTouched(true);
                setSubject(e.target.value);
              }}
              maxLength={120}
            />
          </label>
        ) : null}

        <label className="invite-share-modal__field">
          <span>Your message</span>
          <textarea
            value={message}
            onChange={(e) => {
              setMessageTouched(true);
              setMessage(e.target.value);
            }}
            rows={channel.id === "email" ? 8 : 5}
            maxLength={channel.id === "twitter" ? 280 : 800}
          />
        </label>

        <div className="invite-share-modal__link-row">
          <div>
            <p className="invite-share-modal__link-label">Invite link</p>
            <code className="invite-share-modal__link">{inviteUrl}</code>
          </div>
          <button
            type="button"
            className="invite-share-modal__btn invite-share-modal__btn--ghost"
            onClick={handleCopyLink}
          >
            Copy link
          </button>
        </div>

        {status ? (
          <p className="invite-share-modal__status">{status}</p>
        ) : null}

        <footer className="invite-share-modal__actions">
          <button
            type="button"
            className="invite-share-modal__btn invite-share-modal__btn--ghost"
            onClick={handleCopyOnly}
          >
            Copy message
          </button>
          <button
            type="button"
            className="invite-share-modal__btn invite-share-modal__btn--primary"
            onClick={handlePrimary}
            disabled={busy}
          >
            {busy ? "Opening…" : channel.cta}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default InviteShareModal;
